import { FC, ReactNode, useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import ExitTicketIconXl from '../../../assets/exit-ticket-icon-xl.svg?react';
import ExitTicketInferredIconXl from '../../../assets/exit-ticket-inferred-icon-xl.svg?react';
import ExitTicketInferredDone from '../../../assets/exit-ticket-inferred-done.svg?react';
import ExitTicketSpeedRays from '../../../assets/exit-ticket-speed-rays.svg?react';
// import EphemeralMessageStrokeTop from '../../StudentSingleSkill/EphemeralMessageStrokeTop';
import FilledIceCube from './FilledIceCube';
import EphemeralMessageStrokeTopRight from '../../StudentSingleSkill/EphemeralMessageStrokeTopRight';

/**
 * The celebration that replaces the plain result read-out when the exit ticket the student has just
 * passed was the last one standing between them and a skill (see findSkillEarningExitSlipIds, which
 * is also what puts the "Chance to earn a skill!" sticker on the banner beforehand).
 *
 * Four steps: the ice cubes they just filled spin away, the exit ticket arrives and takes a bow, it
 * resolves into the inferred-skill mark at speed, and finally into the demonstrated mark.
 *
 * Built out of motion rather than a Lottie export so the artwork stays the same SVGs the rest of the
 * feature uses and the copy stays real text — the badge tooltips and the collapsed banner are already
 * hand-animated the same way, down to the overshoot easing below.
 */

// The design's overshoot, shared with DashboardExitSlipModalResult and IceCubesResult so the new
// beats land with the same weight as the ones they follow.
const OVERSHOOT_EASE = [0.34, 1.56, 0.64, 1] as const;

// How far off-centre a beat starts and ends. Every beat travels the same way — in from the right,
// out to the left — so the sequence reads as one thing moving past the student rather than as four
// unrelated things appearing. Kept to a translate inside the already-centred box: an offset large
// enough to start at the true screen edge would widen the modal and bring a scrollbar with it.
const SLIDE_DISTANCE_PX = 80;

// How long a beat takes to travel in and settle. The sway and the rays below are timed to start from
// it, so a change here carries them along instead of leaving them firing over a moving target.
const ARRIVAL_SECONDS = 0.5;

// One full turn of the ice cubes. Long enough to register as a handover rather than a twitch; the
// cubes beat's holdMs must stay clear of this plus the stagger below, or the spin is cut off by the
// next beat.
const CUBE_SPIN_SECONDS = 1.4;
const CUBE_SPIN_STAGGER_SECONDS = 0.12;

// The ticket's bow once it reaches centre: two swings either side of centre, ending exactly where it
// landed. Carrying on along the axis it arrived on reads as the same object still moving, which a
// rotation or a bounce would not.
const SWAY_DISTANCE_PX = 12;
const SWAY_SECONDS = 1;
const SWAY_KEYFRAMES = [0, -SWAY_DISTANCE_PX, SWAY_DISTANCE_PX, -SWAY_DISTANCE_PX, SWAY_DISTANCE_PX, 0];

// The speed rays trailing the inferred mark. They are only true while it is moving, so they flash out
// over its arrival and are gone by the time it sits still.
const RAYS_SECONDS = 0.9;

// Where the bubble's tail sits, in EphemeralMessageStrokeTop's own terms. Top-right, clear of the
// rounded corner, and far enough above the bubble to read as coming out of it: the tail is 38px tall,
// so -20px leaves roughly half of it overlapping the bubble's own border.
const BUBBLE_TAIL_POSITION = { top: '-4px', right: '-12px', left: null };

type Phase = 'cubes' | 'ticket' | 'inferred' | 'done';

interface Beat {
  phase: Phase;
  // How long this beat holds before the next one takes over. The last beat's hold is what the student
  // is left looking at, so it runs until they close the modal — see the scheduler below.
  holdMs: number;
  // The line above the artwork.
  heading?: ReactNode;
  // A line below the artwork, in a speech bubble. A beat carries one or the other, never both: two
  // messages at once would split the student's attention at the moment the artwork changes.
  bubble?: ReactNode;
}

/**
 * The whole timeline, in one place, because it is the part most likely to be retuned against the
 * design: every duration and every line the sequence says lives here rather than being spread across
 * the JSX.
 *
 * Five beats for the design's four steps: step 3 says two things over the one illustration, and a
 * beat is one thing said. Consecutive beats sharing a `phase` leave the artwork mounted — the
 * AnimatePresence below is keyed by phase — so the second message arrives without the inferred mark
 * flying out and back in.
 *
 * TODO(design): the cubes beat's line and the hold durations are placeholders; the motion spec has
 * not been handed over. Retuning the sequence should not need anything below this table to change.
 */
export const SKILL_EARNED_BEATS: Beat[] = [
  {
    phase: 'cubes',
    // Covers the staggered spin (CUBE_SPIN_SECONDS plus the last cube's stagger) with room to settle
    // before the ticket takes over.
    holdMs: 2200,
    heading: (
      <>
        You got them <span className="tw-font-bold">all right</span>
      </>
    ),
  },
  {
    phase: 'ticket',
    // Arrival, then the full sway, then a moment sitting still before it hands over.
    holdMs: (ARRIVAL_SECONDS + SWAY_SECONDS + 0.5) * 1000,
    heading: (
      <>
        <span className="tw-font-bold">Exit Ticket done!</span>
      </>
    ),
  },
  {
    phase: 'inferred',
    holdMs: 1800,
    bubble: <>And guess what?</>,
  },
  {
    phase: 'inferred',
    holdMs: 2600,
    bubble: <>you practiced a skill through Exit tickets</>,
  },
  {
    phase: 'done',
    holdMs: Infinity,
    bubble: <>and now you Demonstrated it!</>,
  },
];

interface Props {
  // How many ice cubes to spin away — the questions the student got right on this ticket.
  correctCount: number;
}

const SkillEarnedSequence: FC<Props> = ({ correctCount }) => {
  const reduceMotion = useReducedMotion();
  const [beatIndex, setBeatIndex] = useState(0);
  const beat = SKILL_EARNED_BEATS[beatIndex];

  // One timer per beat rather than a chain of absolute delays: each beat only has to know its own
  // duration, so retuning one in the table above cannot leave the rest of the sequence out of step.
  useEffect(() => {
    if (!Number.isFinite(beat.holdMs)) {
      return;
    }
    const timer = setTimeout(() => setBeatIndex(index => index + 1), beat.holdMs);
    return () => clearTimeout(timer);
  }, [beat]);

  // With motion reduced the sequence still tells its story — the beats and their lines still arrive in
  // order — it just stops spinning, sliding, swaying and overshooting on the way.
  // In from the right, settling at centre, out to the left.
  const enter = reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8, x: SLIDE_DISTANCE_PX };
  const settle = reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, x: 0 };
  const leave = reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8, x: -SLIDE_DISTANCE_PX };
  const transition = reduceMotion ? { duration: 0 } : { duration: ARRIVAL_SECONDS, ease: OVERSHOOT_EASE };

  // The bubble rises into place from under the artwork, which is where the design has the message
  // coming from, and leaves the same way.
  const bubbleEnter = reduceMotion ? { opacity: 0 } : { opacity: 0, y: 24 };
  const bubbleSettle = { opacity: 1, y: 0 };
  const bubbleLeave = reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12 };

  return (
    // One live region around the whole sequence rather than one per message area. A live region has to
    // be in the document *before* its content changes to be announced reliably, and the two message
    // areas take turns — so a single stable wrapper announces every beat, where a region mounted along
    // with each message would be racing the announcement it is meant to make. The artwork inside is
    // aria-hidden, so nothing but the copy is read out.
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-y-8 tw-w-[300px]" role="status">
      {/* `mode="wait"` so one beat's heading is gone before the next arrives — crossfading two
          sentences in the same spot reads as a glitch rather than as a beat. The fixed height keeps
          the artwork from jumping when a one-line message follows a two-line one, and holds the space
          open on the beats that speak through the bubble instead. */}
      <div className="tw-flex tw-h-16 tw-items-center tw-justify-center">
        <AnimatePresence mode="wait">
          {beat.heading && (
            <motion.p
              key={beat.phase}
              initial={enter}
              animate={settle}
              exit={leave}
              transition={transition}
              className="tw-m-0 tw-text-center tw-font-atkinson tw-text-3xl tw-text-neutral-950"
            >
              {beat.heading}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Tall enough for the tallest piece of artwork (the done mark, at 173px) so the bubble below it
          does not shift up and down as the sequence runs. */}
      <div className="tw-flex tw-h-[180px] tw-items-center tw-justify-center">
        <AnimatePresence mode="wait">
          {beat.phase === 'cubes' && (
            <motion.div key="cubes" exit={leave} transition={transition} className="tw-flex tw-flex-row tw-gap-x-2">
              {Array.from({ length: correctCount }, (_unused, index) => (
                <motion.div
                  key={index}
                  // The spin is the handover: the cubes the student has been looking at turn over and
                  // leave, which is what makes the ticket that follows read as what they became rather
                  // than as a second, unrelated thing appearing.
                  animate={reduceMotion ? undefined : { rotateY: 360 }}
                  transition={
                    reduceMotion
                      ? { duration: 0 }
                      : {
                          duration: CUBE_SPIN_SECONDS,
                          delay: index * CUBE_SPIN_STAGGER_SECONDS,
                          ease: 'easeInOut',
                        }
                  }
                >
                  <FilledIceCube />
                </motion.div>
              ))}
            </motion.div>
          )}

          {beat.phase === 'ticket' && (
            <motion.div key="ticket" initial={enter} animate={settle} exit={leave} transition={transition}>
              {/* The sway is a second, nested transform so it composes with the arrival above instead
                  of competing with it for the same `x`. It waits out the arrival, so the ticket bows
                  from centre rather than mid-flight. */}
              <motion.div
                animate={reduceMotion ? undefined : { x: SWAY_KEYFRAMES }}
                transition={
                  reduceMotion ? { duration: 0 } : { duration: SWAY_SECONDS, delay: ARRIVAL_SECONDS, ease: 'easeInOut' }
                }
              >
                <ExitTicketIconXl aria-hidden="true" />
              </motion.div>
            </motion.div>
          )}

          {beat.phase === 'inferred' && (
            <motion.div
              key="inferred"
              initial={enter}
              animate={settle}
              exit={leave}
              transition={transition}
              className="tw-relative"
            >
              <ExitTicketInferredIconXl aria-hidden="true" />
              {/* Trailing the mark, so on the side it came from. Absolute, so the mark itself stays
                  centred in the box above rather than being pushed off-centre by its own rays.
                  The placement and the motion are two elements on purpose: Tailwind centres this one
                  with a `transform`, and motion writes `transform` inline on whatever it animates —
                  put both on one element and the animation silently drops the centring. */}
              {!reduceMotion && (
                <div className="tw-absolute tw-left-full tw-top-1/2 tw-ml-2 tw--translate-y-1/2">
                  <motion.div
                    initial={{ opacity: 0, x: SLIDE_DISTANCE_PX / 2 }}
                    animate={{ opacity: [0, 1, 1, 0], x: 0 }}
                    transition={{
                      // Travels in with the mark, then flashes out on its own clock.
                      x: { duration: ARRIVAL_SECONDS, ease: 'easeOut' },
                      opacity: { duration: RAYS_SECONDS, times: [0, 0.25, 0.6, 1], ease: 'easeOut' },
                    }}
                  >
                    <ExitTicketSpeedRays aria-hidden="true" />
                  </motion.div>
                </div>
              )}
            </motion.div>
          )}

          {beat.phase === 'done' && (
            <motion.div key="done" initial={enter} animate={settle} exit={leave} transition={transition}>
              <ExitTicketInferredDone aria-hidden="true" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* The bubble's own AnimatePresence, deliberately *without* `mode="wait"`: the design has the
          first message fading out while the second is already on its way in, which is the default
          sync behaviour. Keyed by beat rather than by phase, so the two messages of step 3 swap even
          though the artwork behind them does not change. */}
      <div className="tw-relative tw-h-24 tw-w-full">
        <AnimatePresence>
          {beat.bubble && (
            <motion.div
              key={beatIndex}
              initial={bubbleEnter}
              animate={bubbleSettle}
              exit={bubbleLeave}
              transition={transition}
              // Full-width and absolute so the outgoing message and the incoming one occupy the same
              // spot while they cross. Left in the flow they would sit side by side for the length of
              // the crossfade and shunt each other across the screen. The bubble itself is the child,
              // so its centring is a layout rule motion's inline transform cannot overwrite.
              className="tw-absolute tw-inset-x-0 tw-top-0 tw-flex tw-justify-center"
            >
              {/* 296px is the width the design's longest line needs to sit on one row, and failing
                  that on two. `relative` so the tail below anchors to the bubble rather than to the
                  full-width track it is centred on. */}
              <span className="tw-relative tw-max-w-[300px] tw-rounded-3xl tw-border-[4px] tw-border-solid tw-border-neutral-100 tw-bg-stone-0 tw-px-[18px] tw-py-3 tw-text-center tw-font-atkinson tw-text-lg tw-font-bold tw-text-neutral-950">
                {beat.bubble}
                {/* The same tail the ephemeral messages on the skill page use, pointing back up at
                    the artwork from the bubble's top-right corner. Reused rather than redrawn so a
                    change to the shape reaches both places at once. */}
                <EphemeralMessageStrokeTopRight position={BUBBLE_TAIL_POSITION} stroke />
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SkillEarnedSequence;
